import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { Account, AccountType } from '@/types';


export function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { idToken } = useCognitoAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    code: string;
    type: AccountType;
    is_active: boolean;
  }>({
    name: '',
    code: '',
    type: 'ASSET',
    is_active: true,
  });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!idToken) return;
    async function fetchAccount() {
      if (!id) return;
      try {
        const data = await api.get<Account>(`/api/accounts/${id}`, { errorCode: ErrorCode.FETCH_ACCOUNT, token: idToken! });
        setAccount(data);
        setFormData({
          name: data.name,
          code: data.code,
          type: data.type,
          is_active: data.is_active ?? true,
        });
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch account');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchAccount();
  }, [id, retryKey, idToken]);

  const handleSave = useCallback(async () => {
    if (!account || !idToken) return;
    setSaveError(null);
    try {
      await api.patch<Account>(`/api/accounts/${account.id}`, formData, { errorCode: ErrorCode.UPDATE_ACCOUNT, token: idToken });
      setAccount({ ...account, ...formData });
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to save changes');
    }
  }, [account, formData, idToken]);

  const handleBack = useCallback(() => {
    void navigate('/accounts');
  }, [navigate]);

  const handleEdit = useCallback(() => {
    setEditing(true);
    setSaveError(null);
  }, []);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setSaveError(null);
  }, []);

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, code: e.target.value }));
  }, []);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, type: e.target.value }));
  }, []);

  const handleActiveChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, is_active: e.target.checked }));
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading account...</p>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error ?? 'Account not found'}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-start pb-4 border-b border-border">
        <div className="flex flex-col gap-2">
          <Button variant="ghost" size="sm" onClick={handleBack} className="w-fit">
            <i className="bi bi-arrow-left"></i> Back
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">
              <code className="text-sm bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
              {account.name}
            </h1>
            <Badge variant={account.is_active ? 'secondary' : 'default'}>
              {account.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
        <div className="flex gap-3">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            <Button variant="default" size="sm" onClick={handleEdit}>
              <i className="bi bi-pencil"></i> Edit
            </Button>
          )}
        </div>
      </div>

      {saveError && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">{saveError}</div>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">Type</span>
              <span className="font-semibold">{account.type}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">Subtype</span>
              <span className="font-semibold">{account.subtype ?? '—'}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">Balance</span>
              <span className="font-semibold font-mono text-muted-foreground">—</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>Edit Account</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">Code</label>
                <input
                  type="text"
                  className="px-3 py-2 text-sm border border-input rounded-md bg-background"
                  value={formData.code}
                  onChange={handleCodeChange}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">Name</label>
                <input
                  type="text"
                  className="px-3 py-2 text-sm border border-input rounded-md bg-background"
                  value={formData.name}
                  onChange={handleNameChange}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">Type</label>
                <select
                  className="px-3 py-2 text-sm border border-input rounded-md bg-background"
                  value={formData.type}
                  onChange={handleTypeChange}
                >
                  <option value="ASSET">Asset</option>
                  <option value="LIABILITY">Liability</option>
                  <option value="EQUITY">Equity</option>
                  <option value="REVENUE">Revenue</option>
                  <option value="EXPENSE">Expense</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  className="h-4 w-4"
                  checked={formData.is_active}
                  onChange={handleActiveChange}
                />
                <label htmlFor="is_active" className="text-sm font-medium text-foreground">Active</label>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="text-4xl text-muted-foreground mb-3">
              <i className="bi bi-receipt"></i>
            </div>
            <p className="text-muted-foreground">
              Transaction history will appear here
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
